import { useEffect, useRef, useState } from 'react'
import { Plus } from 'lucide-react'

interface Props {
  onAdd: (text: string) => void
  label?: string
  placeholder?: string
  /** The chip wears the same classes as its neighbours; the caller passes them. */
  chipClassName: string
  inputClassName?: string
  testId?: string
}

/**
 * A "+ Another…" chip that turns into a short text box (owner 2026-09-05: life
 * areas are "selectable and also addable"). Enter or Add commits; Escape, or
 * leaving it empty, puts the chip back. It never submits the surrounding form
 * and never closes the popover it sits in.
 */
export function AddOwnChip({ onAdd, label = 'Another…', placeholder = 'Name it', chipClassName, inputClassName, testId = 'add-own' }: Props) {
  const [open, setOpen] = useState(false)
  const [text, setText] = useState('')
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => { if (open) ref.current?.focus() }, [open])
  const commit = () => {
    const t = text.trim()
    if (t) onAdd(t)
    setText('')
    setOpen(false)
  }
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className={chipClassName} data-testid={testId}>
        <Plus className="mr-1 inline h-3.5 w-3.5" aria-hidden="true" />{label}
      </button>
    )
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      <input
        ref={ref}
        type="text"
        value={text}
        maxLength={40}
        onChange={(e) => setText(e.target.value)}
        placeholder={placeholder}
        aria-label={label}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); commit() }
          else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setText(''); setOpen(false) }
        }}
        onBlur={() => { if (!text.trim()) setOpen(false) }}
        className={inputClassName || 'w-44 rounded-full border border-strong bg-surface px-3.5 py-2 text-sm text-primary placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-brand/30'}
        data-testid={`${testId}-input`}
      />
      <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={commit} className="text-sm font-medium text-brand hover:underline" data-testid={`${testId}-commit`}>Add</button>
    </span>
  )
}

export default AddOwnChip
